/**
 * Brand kit — logo downloads + copy-to-clipboard swatches (sections/brand-kit).
 *
 * Downloads: each link points at the theme's logo SVG, so it works without JS.
 * With JS, SVG is fetched and saved under a friendly filename, and PNG is
 * rasterized from that same vector at print size (transparent background) —
 * one source file per mark, no PNGs to keep in sync.
 */
(() => {
  const save = (blob, name) => {
    const url = URL.createObjectURL(blob);
    const a = Object.assign(document.createElement('a'), { href: url, download: name });
    document.body.append(a);
    a.click();
    a.remove();
    setTimeout(() => URL.revokeObjectURL(url), 1000);
  };

  // Give the SVG explicit pixel dimensions so every browser rasterizes it at
  // the target size (an SVG with only a viewBox draws at 300×150 in Firefox).
  const toPng = (svgText, width) =>
    new Promise((resolve, reject) => {
      const doc = new DOMParser().parseFromString(svgText, 'image/svg+xml');
      const svg = doc.documentElement;
      const [, , vw, vh] = svg.getAttribute('viewBox').split(/[\s,]+/).map(Number);
      const height = Math.round((width * vh) / vw);
      svg.setAttribute('width', width);
      svg.setAttribute('height', height);
      const src = URL.createObjectURL(
        new Blob([new XMLSerializer().serializeToString(svg)], { type: 'image/svg+xml' })
      );
      const img = new Image();
      img.onload = () => {
        const canvas = Object.assign(document.createElement('canvas'), { width, height });
        canvas.getContext('2d').drawImage(img, 0, 0, width, height);
        URL.revokeObjectURL(src);
        canvas.toBlob((blob) => (blob ? resolve(blob) : reject(new Error('PNG export failed'))), 'image/png');
      };
      img.onerror = reject;
      img.src = src;
    });

  document.addEventListener('click', async (event) => {
    const link = event.target.closest('[data-brand-download]');
    if (!link) return;
    event.preventDefault();
    if (link.getAttribute('aria-busy') === 'true') return;
    link.setAttribute('aria-busy', 'true');

    const { brandDownload: format, fileName, pngWidth } = link.dataset;
    try {
      const svgText = await (await fetch(link.href)).text();
      if (format === 'png') {
        save(await toPng(svgText, Number(pngWidth) || 3000), `${fileName}.png`);
      } else {
        save(new Blob([svgText], { type: 'image/svg+xml' }), `${fileName}.svg`);
      }
    } catch (error) {
      window.open(link.href, '_blank', 'noopener'); // fall back to the raw SVG
    } finally {
      link.removeAttribute('aria-busy');
    }
  });

  document.addEventListener('click', async (event) => {
    const button = event.target.closest('[data-brand-copy]');
    if (!button) return;
    const label = button.querySelector('[data-brand-copy-text]');
    try {
      await navigator.clipboard.writeText(button.dataset.brandCopy);
    } catch (error) {
      return;
    }
    button.classList.add('is-copied');
    label.textContent = 'Copied';
    setTimeout(() => {
      button.classList.remove('is-copied');
      label.textContent = button.dataset.brandCopy;
    }, 1400);
  });
})();
